import type { Client } from './types';
import type { Product, ProductKey } from './modules';
import { clientHasModule, firstAvailableProduct, PRODUCT_MODULE, PRODUCTS } from './modules';

/**
 * Persists the last product (top-level route segment: `find-and-fix` / `savi`) the user
 * actively viewed, so a bare load with no product in the URL can restore it.
 *
 * The product is otherwise carried by the URL path (`/savi/*`, `/find-and-fix/*`); only the
 * root (`/`) landing has no product to read, which is where the remembered value is used.
 */
const LAST_PRODUCT_KEY = 'orbit:lastProduct';

const VALID_PRODUCT_KEYS: ReadonlySet<string> = new Set(PRODUCTS.map((p) => p.key));

/** The last product the user viewed, or null if none stored / the stored value is invalid. */
export function loadLastProduct(): ProductKey | null {
    try {
        const raw = localStorage.getItem(LAST_PRODUCT_KEY);
        return raw !== null && VALID_PRODUCT_KEYS.has(raw) ? (raw as ProductKey) : null;
    } catch {
        // localStorage may be unavailable (private browsing, storage quota, etc.)
        return null;
    }
}

/** Records the active product. Silently ignores write failures. */
export function saveLastProduct(product: ProductKey): void {
    try {
        localStorage.setItem(LAST_PRODUCT_KEY, product);
    } catch {
        // localStorage may be unavailable (private browsing, storage quota, etc.)
    }
}

/**
 * Picks the product to land on when the URL provides none (the `/` route).
 *
 * Priority:
 *   1. The remembered product, if the active client has its module.
 *   2. The first product the client does have a module for.
 *   3. `find-and-fix` as a final fallback (e.g. no client / no modules).
 *
 * This keeps a bare load on the user's last product when possible, but never strands them
 * on a product their current client can't use (which would otherwise show an upsell).
 */
export function resolveLandingProduct(remembered: ProductKey | null, client: Client | null): ProductKey {
    if (remembered && clientHasModule(client, PRODUCT_MODULE[remembered])) {
        return remembered;
    }
    return firstAvailableProduct(client)?.key ?? 'find-and-fix';
}

/**
 * The route for {@link resolveLandingProduct}'s answer — where a bare `/` load lands, and
 * where choosing a client from inside Network Explorer takes the user.
 */
export function resolveLandingRoute(remembered: ProductKey | null, client: Client | null): Product['to'] {
    const target = resolveLandingProduct(remembered, client);
    return PRODUCTS.find((p) => p.key === target)?.to ?? '/find-and-fix';
}
